import React, { forwardRef, useState } from 'react'
import Downshift from 'downshift'
import classnames from 'classnames/bind'

import { IMedkitForwardRefRenderFunction, Spinner } from '../..'
import Suggestion from './Suggestion'
import BaseInput from '../BaseInput'
import { AutoSuggestOption } from './types'
import { autoSuggestTheme } from './theme'
import LiveRegion from './LiveRegion'
import styles from './index.module.scss'
import { BaseInputProps } from '../BaseInput/BaseInput'

const cx = classnames.bind(styles)

const AutoSuggest: IMedkitForwardRefRenderFunction<
  HTMLInputElement,
  IProps,
  typeof autoSuggestTheme
> = (
  {
    inputValue,
    loading,
    isOpen,
    label,
    legend,
    numOfSuggestionsText,
    onInputChange,
    onSelect,
    options,
    placeholder,
    endAdornment,
    disabled,
    ErrorMessages,
    helpText,
    onFocus,
    onBlur,
    error,
  }: IProps,
  ref
) => {
  const [isInputFocused, updateFocus] = useState(false)

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    updateFocus(false)
    onBlur?.(event)
  }

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    updateFocus(true)
    onFocus?.(event)
  }

  return (
    <>
      {numOfSuggestionsText && (
        <LiveRegion
          numOfSuggestionsText={options.length ? numOfSuggestionsText : ''}
        />
      )}
      <fieldset className={cx('autoSuggest')}>
        {legend && (
          <legend className={cx('autoSuggest__legend')}>{legend}</legend>
        )}
        <Downshift
          onSelect={onSelect}
          itemToString={(item) => (item ? item.label : '')}
          isOpen={isOpen}
          inputValue={inputValue}
        >
          {({
            getInputProps,
            getItemProps,
            getLabelProps,
            getMenuProps,
            getRootProps,
            isOpen: _isOpen,
            highlightedIndex,
          }) => {
            const isDropdownOpen = _isOpen && options.length > 0
            const inputProps = {
              ...getInputProps({
                endAdornment: loading ? (
                  <Spinner variant="primary" />
                ) : (
                  endAdornment
                ),
                onChange: onInputChange,
                label,
                placeholder,
                className: cx(styles.inputWrapper, {
                  openInput: isDropdownOpen,
                }),
                inputClassName: cx({ input: isDropdownOpen }),
                onFocus: handleFocus,
                onBlur: handleBlur,
                disabled,
              }),
              ...getRootProps(undefined, { suppressRefError: true }),
            }
            const labelProps = getLabelProps()
            const menuProps = getMenuProps(
              {
                className: cx('autoSuggest__suggestions', {
                  'autoSuggest__suggestions--is-focused': isInputFocused,
                }),
              },
              { suppressRefError: true }
            )

            return (
              <>
                <BaseInput
                  {...inputProps}
                  labelProps={labelProps}
                  ref={ref}
                  data-testid="text-input"
                  helpText={helpText}
                  ErrorMessages={ErrorMessages}
                  error={error}
                />
                {isOpen && (
                  <ul {...menuProps} data-testid="auto-suggest-dropdown">
                    {options.map((item: AutoSuggestOption, index: number) => (
                      <li
                        key={item.id || item.label}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...getItemProps({
                          index,
                          item,
                          className: cx('autoSuggest__suggestion', {
                            'autoSuggest__suggestion--selected':
                              highlightedIndex === index,
                          }),
                        })}
                      >
                        <Suggestion item={item} />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )
          }}
        </Downshift>
      </fieldset>
    </>
  )
}

AutoSuggest.theme = autoSuggestTheme

export interface IProps
  extends Omit<BaseInputProps<HTMLInputElement>, 'onSelect'> {
  /* The options to display in the select dropdown */
  options: AutoSuggestOption[]
  /* What to do when the input changes (e.g. fetch more results) */
  onInputChange: (e: any) => void
  /* What to do when a value is selected - Gets the whole option object as an argument */
  onSelect: (item: AutoSuggestOption | null) => void
  /* Is the input loading e.g. fetching results */
  loading?: boolean
  /* The label for the autoSuggest input */
  label: string
  /* The legend for the whole autoSuggest */
  legend?: string
  /* The text to display for how many suggestions are available */
  numOfSuggestionsText?: string
  /* Force the dropdown select to be open/closed */
  isOpen: boolean
  /* The value of the text input */
  inputValue: string
  /* The placeholder text for the input */
  placeholder?: string
  endAdornment?: React.ReactChild
  /* Is the input disabled */
  disabled?: boolean
}

export default forwardRef<HTMLInputElement, IProps>(AutoSuggest)
